import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { aiAPI } from '../lib/api';
import { useUserLocation } from '../hooks/useUserLocation';
import { Sparkles, X, Send, Loader2, MessageCircle, Tag, Navigation, Store as StoreIcon, MapPin, ChevronRight } from 'lucide-react';

const STORAGE_KEY = 'destack_ai_session';

const ensureSession = () => {
  let s = localStorage.getItem(STORAGE_KEY);
  if (!s) {
    s = `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(STORAGE_KEY, s);
  }
  return s;
};

const SUGGESTIONS = [
  'Quais são as promoções do dia?',
  'Tem moda feminina?',
  'Como comprar no marketplace?',
  'Lojas perto de mim',
];

export const AIAssistant = () => {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(ensureSession);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const { position } = useUserLocation(false);

  useEffect(() => {
    if (!open) return;
    aiAPI
      .history(sessionId)
      .then(({ data }) => setMessages(data.messages || []))
      .catch(() => {});
  }, [open, sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content }]);
    setSending(true);
    try {
      const extra = position ? { lat: position.lat, lng: position.lng } : {};
      const { data } = await aiAPI.chat(sessionId, content, extra);
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content: data.reply,
          products: data.products || [],
          stores: data.stores || [],
          category_chip: data.category_chip || null,
          intent: data.intent,
        },
      ]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content:
            err.response?.data?.detail ||
            'Desculpe, não consegui responder agora. Tente novamente em instantes.',
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-24 right-4 sm:bottom-6 sm:right-6 z-50 group"
        data-testid="ai-assistant-open"
        aria-label="Abrir Assistente IA"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-violet-600 rounded-full blur-lg opacity-50 group-hover:opacity-75 transition-opacity animate-pulse" />
        <div className="relative w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 via-blue-600 to-violet-600 shadow-2xl flex items-center justify-center text-white group-hover:scale-110 transition-transform">
          <Sparkles className="w-6 h-6" />
        </div>
        <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-white" />
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-0 right-0 sm:bottom-6 sm:right-6 z-50 w-full sm:w-[400px] h-[80vh] sm:h-[600px] sm:rounded-2xl bg-white shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
      data-testid="ai-assistant-panel"
    >
      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-blue-600 to-violet-600 text-white flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
            <Sparkles className="w-4 h-4" />
          </div>
          <div className="leading-tight">
            <p className="font-semibold text-sm">Assistente DESTACK</p>
            <p className="text-[11px] text-blue-100 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              Online • Powered by IA
            </p>
          </div>
        </div>
        <button
          onClick={() => setOpen(false)}
          className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
          aria-label="Fechar"
          data-testid="ai-assistant-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50">
        {messages.length === 0 && !sending && (
          <div className="text-center py-6">
            <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-100 to-violet-100 flex items-center justify-center mb-3">
              <MessageCircle className="w-7 h-7 text-blue-600" />
            </div>
            <p className="text-slate-900 font-semibold text-sm">Olá! 👋</p>
            <p className="text-slate-500 text-xs mt-1 mb-4">
              Como posso te ajudar a encontrar o que procura?
            </p>
            <div className="grid grid-cols-1 gap-2 max-w-xs mx-auto">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="text-left text-xs px-3 py-2 rounded-xl bg-white border border-slate-200 hover:border-blue-400 hover:text-blue-700 transition-colors"
                  data-testid={`ai-suggestion-${s.slice(0, 10)}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className="space-y-2">
            <div className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[85%] px-3.5 py-2 rounded-2xl text-sm leading-relaxed ${
                  m.role === 'user'
                    ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-br-sm'
                    : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm shadow-sm'
                }`}
              >
                {m.content}
              </div>
            </div>
            {/* Category chip (clickable) */}
            {m.role === 'assistant' && m.category_chip && (
              <Link
                to={`/categoria/${m.category_chip.id}`}
                className="inline-flex items-center gap-2 mt-1 px-3 py-1.5 rounded-full bg-gradient-to-r from-blue-50 to-violet-50 border border-blue-200 text-blue-700 text-xs font-medium hover:from-blue-100 hover:to-violet-100 transition-colors"
                data-testid="ai-category-chip"
              >
                {m.category_chip.icon && <span>{m.category_chip.icon}</span>}
                Ver categoria {m.category_chip.name}
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            )}
            {/* Product cards rendered inside the assistant message */}
            {m.role === 'assistant' && m.products && m.products.length > 0 && (
              <div
                className="flex gap-2.5 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory"
                data-testid="ai-product-cards"
              >
                {m.products.map((p) => (
                  <ProductCardInline key={p.id} product={p} />
                ))}
              </div>
            )}
            {/* Store cards */}
            {m.role === 'assistant' && m.stores && m.stores.length > 0 && (
              <div
                className="flex gap-2.5 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory"
                data-testid="ai-store-cards"
              >
                {m.stores.map((s) => (
                  <StoreCardInline key={s.id} store={s} />
                ))}
              </div>
            )}
          </div>
        ))}

        {sending && (
          <div className="flex justify-start">
            <div className="px-4 py-2.5 rounded-2xl bg-white border border-slate-200 shadow-sm rounded-bl-sm">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        className="px-3 py-3 border-t border-slate-200 bg-white flex items-end gap-2"
      >
        <textarea
          rows={1}
          placeholder="Digite sua pergunta..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          disabled={sending}
          className="flex-1 resize-none text-sm px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all max-h-32"
          data-testid="ai-input"
        />
        <button
          type="submit"
          disabled={sending || !input.trim()}
          className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-violet-600 text-white flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:scale-105 transition-transform"
          data-testid="ai-send"
          aria-label="Enviar"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </div>
  );
};

export default AIAssistant;

const ProductCardInline = ({ product }) => {
  const hasDiscount =
    product.original_price && product.price && product.original_price > product.price;
  const discount = hasDiscount
    ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
    : 0;
  const fmt = (v) =>
    typeof v === 'number'
      ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
      : v;
  return (
    <Link
      to={`/produto/${product.id}`}
      className="snap-start flex-shrink-0 w-40 bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-blue-400 hover:shadow-md transition-all"
      data-testid={`ai-product-${product.id}`}
    >
      <div className="relative aspect-square bg-slate-100">
        {product.image ? (
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300">
            <Tag className="w-8 h-8" />
          </div>
        )}
        {hasDiscount && (
          <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-md bg-rose-600 text-white text-[10px] font-bold">
            -{discount}%
          </span>
        )}
      </div>
      <div className="p-2 space-y-0.5">
        <p className="text-xs font-medium text-slate-900 line-clamp-2 leading-snug min-h-[2rem]">
          {product.name}
        </p>
        {product.store_name && (
          <p className="text-[10px] text-slate-500 truncate">{product.store_name}</p>
        )}
        {product.distance_km != null && (
          <p className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-blue-700">
            <Navigation className="w-2.5 h-2.5" /> {product.distance_km} km
          </p>
        )}
        <div className="flex items-baseline gap-1 pt-1">
          {hasDiscount && (
            <span className="text-[10px] text-slate-400 line-through">
              {fmt(product.original_price)}
            </span>
          )}
          <span className="text-sm font-bold text-blue-700">{fmt(product.price)}</span>
        </div>
      </div>
    </Link>
  );
};

const StoreCardInline = ({ store }) => {
  return (
    <Link
      to={`/loja/${store.id}`}
      className="snap-start flex-shrink-0 w-44 bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-blue-400 hover:shadow-md transition-all"
      data-testid={`ai-store-${store.id}`}
    >
      <div className="relative aspect-[3/1.4] bg-gradient-to-br from-blue-100 to-violet-100">
        {store.banner_url ? (
          <img src={store.banner_url} alt={store.name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-blue-400">
            <StoreIcon className="w-7 h-7" />
          </div>
        )}
        {store.logo_url && (
          <img
            src={store.logo_url}
            alt=""
            className="absolute -bottom-3 left-2 w-9 h-9 rounded-lg border-2 border-white object-cover bg-white"
          />
        )}
      </div>
      <div className="p-2 pt-3 space-y-0.5">
        <p className="text-xs font-semibold text-slate-900 line-clamp-1 leading-snug">{store.name}</p>
        {(store.city || store.state) && (
          <p className="text-[10px] text-slate-500 inline-flex items-center gap-0.5">
            <MapPin className="w-2.5 h-2.5" />
            {store.city}{store.state ? ` / ${store.state}` : ''}
          </p>
        )}
        {store.distance_km != null && (
          <p className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded-full mt-1">
            <Navigation className="w-2.5 h-2.5" /> {store.distance_km} km de você
          </p>
        )}
      </div>
    </Link>
  );
};
